// RecurringTransactionDialog.js
// Dialog for creating/editing recurring transactions

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  FormControlLabel,
  Switch,
  Divider,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MobileDialog from './MobileDialog';
import CreateTagDialog from './CreateTagDialog';
import {
  getGeneralTransactionTags,
  createRecurringTransaction,
  updateRecurringTransaction,
} from '../../services/cashflowService';

const RecurringTransactionDialog = ({
  open,
  onClose,
  onSuccess,
  editData = null, // Pass existing recurring transaction for editing
}) => {
  const isEditMode = !!editData;

  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState('expense');
  const [selectedTagId, setSelectedTagId] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);
  const [hasEndDate, setHasEndDate] = useState(false);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [isActive, setIsActive] = useState(true);

  // UI state
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateTagDialog, setShowCreateTagDialog] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);

  const queryClient = useQueryClient();

  // Fetch tags based on selected type
  const { data: tags = [], isLoading: isLoadingTags, refetch: refetchTags } = useQuery({
    queryKey: ['general-transaction-tags', type],
    queryFn: () => getGeneralTransactionTags(type),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Initialize form with edit data
  useEffect(() => {
    if (open && editData) {
      setName(editData.Name || '');
      setType(editData.Type || 'expense');
      setSelectedTagId(editData.Tag || '');
      setAmount(editData.Amount?.toString() || '');
      setFrequency(editData.Frequency || 'monthly');
      setDayOfMonth(editData.DayOfMonth || 1);
      setStartDate(editData.StartDate ? new Date(editData.StartDate) : new Date());
      setEndDate(editData.EndDate ? new Date(editData.EndDate) : null);
      setHasEndDate(!!editData.EndDate);
      setDefaultPaymentMethod(editData.DefaultPaymentMethod || 'cash');
      setNote(editData.Note || '');
      setIsActive(editData.IsActive ?? true);
    }
  }, [open, editData]);

  // Scroll to error when it appears
  useEffect(() => {
    if (localError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [localError]);

  // Reset form state
  const resetForm = () => {
    setName('');
    setType('expense');
    setSelectedTagId('');
    setAmount('');
    setFrequency('monthly');
    setDayOfMonth(1);
    setStartDate(new Date());
    setEndDate(null);
    setHasEndDate(false);
    setDefaultPaymentMethod('cash');
    setNote('');
    setIsActive(true);
    setLocalError('');
    setLocalSuccess('');
    setShowSummary(false);
  };

  // Get selected tag name
  const selectedTagName = useMemo(() => {
    if (!selectedTagId) return 'ללא קטגוריה';
    const tag = tags.find(t => t.TagID === selectedTagId);
    return tag?.Name || 'ללא קטגוריה';
  }, [selectedTagId, tags]);

  // Payment method display names
  const paymentMethodLabels = {
    cash: 'מזומן',
    check: "צ'ק",
    credit_card: 'כרטיס אשראי',
    bank_transfer: 'העברה בנקאית',
    other: 'אחר',
  };

  // Validate and go to summary step
  const handleNextStep = () => {
    setLocalError('');

    if (!name.trim()) {
      setLocalError('שגיאה: יש להזין שם לתנועה.');
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setLocalError('שגיאה: הסכום חייב להיות גדול מאפס.');
      return;
    }

    if (frequency === 'monthly' && (dayOfMonth < 1 || dayOfMonth > 31)) {
      setLocalError('שגיאה: יום בחודש חייב להיות בין 1 ל-31.');
      return;
    }

    setShowSummary(true);
  };

  // Go back to form
  const handleBackToForm = () => {
    setShowSummary(false);
    setLocalError('');
  };

  // Handle form close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Handle tag creation success
  const handleTagCreated = (newTag) => {
    refetchTags();
    setSelectedTagId(newTag.TagID);
    setShowCreateTagDialog(false);
  };

  // Submit recurring transaction
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setLocalError('');
    setLocalSuccess('');

    try {
      const payload = {
        name: name.trim(),
        type: type,
        tag_id: selectedTagId || null,
        amount: parseFloat(amount),
        frequency: frequency,
        day_of_month: frequency === 'monthly' ? dayOfMonth : null,
        start_date: startDate,
        end_date: hasEndDate ? endDate : null,
        default_payment_method: defaultPaymentMethod,
        note: note,
        is_active: isActive,
      };

      if (isEditMode) {
        payload.recurring_id = editData.RecurringID;
        await updateRecurringTransaction(payload);
        setLocalSuccess('התנועה החוזרת עודכנה בהצלחה!');
      } else {
        await createRecurringTransaction(payload);
        setLocalSuccess('התנועה החוזרת נוצרה בהצלחה!');
      }

      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
      await queryClient.refetchQueries({ queryKey: ['recurring-transactions'] });

      if (onSuccess) {
        onSuccess();
      }

      // Close dialog after delay
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('שגיאה בשמירת תנועה חוזרת:', err);
      setLocalError(
        err?.response?.data?.error ||
        err.message ||
        'שגיאה בעת שמירת התנועה החוזרת'
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Generate day options 1-31
  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <>
      <MobileDialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleClose();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.4rem' }}>
          {isEditMode ? 'עריכת תנועה חוזרת' : 'יצירת תנועה חוזרת'}
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {localError && (
              <Alert ref={errorRef} severity="error">{localError}</Alert>
            )}
            {localSuccess && (
              <Alert severity="success">{localSuccess}</Alert>
            )}

            {/* Form */}
            {(!showSummary || isEditMode) && (
              <>
            {/* Name */}
            <TextField
              label="שם התנועה"
              type="text"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="לדוגמה: שכירות משרד"
            />

            {/* Type */}
            <FormControl fullWidth>
              <InputLabel>סוג</InputLabel>
              <Select
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setSelectedTagId(''); // Reset tag when type changes
                }}
                label="סוג"
                disabled={isEditMode}
              >
                <MenuItem value="expense">הוצאה</MenuItem>
                <MenuItem value="income">הכנסה</MenuItem>
              </Select>
              {isEditMode && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  לא ניתן לשנות את סוג התנועה לאחר יצירה
                </Typography>
              )}
            </FormControl>

            {/* Tag Selection */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <FormControl fullWidth>
                <InputLabel>קטגוריה</InputLabel>
                <Select
                  value={selectedTagId}
                  onChange={(e) => setSelectedTagId(e.target.value)}
                  label="קטגוריה"
                  disabled={isLoadingTags}
                >
                  <MenuItem value="">
                    <em>ללא קטגוריה</em>
                  </MenuItem>
                  {tags.map((tag) => (
                    <MenuItem key={tag.TagID} value={tag.TagID}>
                      {tag.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                onClick={() => setShowCreateTagDialog(true)}
                sx={{ mt: 1 }}
                title="הוספת קטגוריה חדשה"
              >
                <AddIcon />
              </IconButton>
            </Box>

            {/* Amount */}
            <TextField
              label="סכום"
              type="number"
              fullWidth
              sx={{
                direction: 'ltr',
                '& input': {
                  textAlign: 'center',
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '-moz-appearance': 'textfield',
                },
              }}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onWheel={(e) => e.target.blur()}
              onFocus={(e) => e.target.select()}
            />

            {/* Frequency */}
            <FormControl fullWidth>
              <InputLabel>תדירות</InputLabel>
              <Select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                label="תדירות"
              >
                <MenuItem value="weekly">שבועי (כל יום ראשון)</MenuItem>
                <MenuItem value="monthly">חודשי</MenuItem>
              </Select>
            </FormControl>

            {/* Day of Month (only for monthly) */}
            {frequency === 'monthly' && (
              <FormControl fullWidth>
                <InputLabel>יום בחודש</InputLabel>
                <Select
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                  label="יום בחודש"
                >
                  {dayOptions.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  אם היום גדול ממספר הימים בחודש, יופעל ביום האחרון של החודש
                </Typography>
              </FormControl>
            )}

            {/* Start Date */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
              <DatePicker
                label="תאריך התחלה"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                sx={{direction: 'ltr'}}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  }
                }}
              />
            </LocalizationProvider>

            {/* End Date Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={hasEndDate}
                  onChange={(e) => {
                    setHasEndDate(e.target.checked);
                    if (!e.target.checked) {
                      setEndDate(null);
                    }
                  }}
                />
              }
              label="הגדר תאריך סיום"
            />

            {/* End Date (optional) */}
            {hasEndDate && (
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
                <DatePicker
                  label="תאריך סיום"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  sx={{direction: 'ltr'}}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    }
                  }}
                />
              </LocalizationProvider>
            )}

            {/* Default Payment Method */}
            <FormControl fullWidth>
              <InputLabel>אמצעי תשלום ברירת מחדל</InputLabel>
              <Select
                value={defaultPaymentMethod}
                onChange={(e) => setDefaultPaymentMethod(e.target.value)}
                label="אמצעי תשלום ברירת מחדל"
              >
                <MenuItem value="cash">מזומן</MenuItem>
                <MenuItem value="check">צ'ק</MenuItem>
                <MenuItem value="credit_card">כרטיס אשראי</MenuItem>
                <MenuItem value="bank_transfer">העברה בנקאית</MenuItem>
                <MenuItem value="other">אחר</MenuItem>
              </Select>
            </FormControl>

            {/* Note */}
            <TextField
              label="הערות (אופציונלי)"
              type="text"
              multiline
              minRows={2}
              fullWidth
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            {/* Active Status (for edit mode) */}
            {isEditMode && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="פעיל"
              />
            )}
              </>
            )}

            {/* Summary - only for create mode */}
            {showSummary && !isEditMode && (
              <Paper elevation={0} sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  סיכום התנועה החוזרת
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {/* Name */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">שם התנועה:</Typography>
                    <Typography variant="body1" fontWeight="bold">{name}</Typography>
                  </Box>

                  <Divider />

                  {/* Type */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">סוג:</Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ color: type === 'expense' ? 'error.main' : 'success.main' }}
                    >
                      {type === 'expense' ? 'הוצאה' : 'הכנסה'}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Category */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">קטגוריה:</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedTagName}</Typography>
                  </Box>

                  <Divider />

                  {/* Amount */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">סכום:</Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: type === 'expense' ? 'error.main' : 'success.main' }}
                    >
                      ₪{parseFloat(amount).toLocaleString('he-IL', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Frequency */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">תדירות:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {frequency === 'weekly' ? 'שבועי (כל יום ראשון)' : `חודשי (בכל ${dayOfMonth} לחודש)`}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Start Date */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">תאריך התחלה:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {startDate ? format(startDate, 'dd/MM/yyyy') : '-'}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* End Date */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">תאריך סיום:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {hasEndDate && endDate ? format(endDate, 'dd/MM/yyyy') : 'ללא הגבלה'}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Payment Method */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">אמצעי תשלום:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {paymentMethodLabels[defaultPaymentMethod] || defaultPaymentMethod}
                    </Typography>
                  </Box>

                  {/* Note */}
                  {note && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>הערות:</Typography>
                        <Typography variant="body1">{note}</Typography>
                      </Box>
                    </>
                  )}
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  לאחר יצירת התנועה החוזרת, היא תופעל אוטומטית בהתאם ללוח הזמנים שהגדרת.
                </Alert>
              </Paper>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          {/* Left side - Cancel or Back */}
          <Box>
            {!localSuccess && (
              <>
                {!showSummary || isEditMode ? (
                  <Button
                    onClick={handleClose}
                    color="inherit"
                    size="large"
                    sx={{ fontSize: '1rem' }}
                  >
                    ביטול
                  </Button>
                ) : (
                  <Button
                    onClick={handleBackToForm}
                    color="inherit"
                    size="large"
                    sx={{ fontSize: '1rem' }}
                  >
                    חזור
                  </Button>
                )}
              </>
            )}
          </Box>

          {/* Right side - Next or Submit */}
          <Box>
            {!localSuccess && (
              <>
                {isEditMode ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{ fontSize: '1rem' }}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <CircularProgress size={24} /> : 'עדכן'}
                  </Button>
                ) : !showSummary ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{ fontSize: '1rem' }}
                    onClick={handleNextStep}
                  >
                    הבא
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    sx={{ fontSize: '1rem' }}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <CircularProgress size={24} /> : 'אישור'}
                  </Button>
                )}
              </>
            )}
          </Box>
        </DialogActions>
      </MobileDialog>

      {/* Create Tag Dialog */}
      <CreateTagDialog
        open={showCreateTagDialog}
        onClose={() => setShowCreateTagDialog(false)}
        onSuccess={handleTagCreated}
        defaultType={type}
      />
    </>
  );
};

export default RecurringTransactionDialog;
